import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BoardResponseDto, BoardMemberDto, BoardExecutiveMemberDto, BoardUpsertRequest, BoardMemberDeleteRequest, BoardAllPeriodsResponseDto, BoardAllPeriodsUpsertRequest } from '../../models/api/companyBoardMemberDto';
import { YearlyResource } from '../../models/api/yearlyResource';

export interface BoardContext {
  boardType?: number;
  /** Board members for the requested year — populated regardless of envelope state. */
  boardMembers: BoardMemberDto[];
  /** Executive members for the requested year — populated regardless of envelope state. */
  executiveMembers: BoardExecutiveMemberDto[];
}

@Injectable({
  providedIn: 'root'
})
export class CompanyBoardService {
  private apiUrl = `${environment.apiUrl}/companies`;

  constructor(private http: HttpClient) {}

  getAll(companyId: string, year?: number): Observable<YearlyResource<BoardResponseDto, BoardContext>> {
    let params = new HttpParams();
    if (year !== undefined) {
      params = params.set('year', year.toString());
    }
    return this.http.get<YearlyResource<BoardResponseDto, BoardContext>>(
      `${this.apiUrl}/${companyId}/board`,
      { params }
    );
  }

  getAllPeriods(companyId: string): Observable<BoardAllPeriodsResponseDto> {
    return this.http.get<BoardAllPeriodsResponseDto>(`${this.apiUrl}/${companyId}/board/all-periods`);
  }

  updateAllPeriods(companyId: string, request: BoardAllPeriodsUpsertRequest): Observable<BoardAllPeriodsResponseDto> {
    return this.http.put<BoardAllPeriodsResponseDto>(`${this.apiUrl}/${companyId}/board/all-periods`, request);
  }

  getById(companyId: string, boardMemberId: string, year?: number): Observable<BoardMemberDto> {
    let params = new HttpParams();
    if (year !== undefined) {
      params = params.set('year', year.toString());
    }
    return this.http.get<BoardMemberDto>(`${this.apiUrl}/${companyId}/board/${boardMemberId}`, { params });
  }

  upsert(companyId: string, request: BoardUpsertRequest): Observable<YearlyResource<BoardResponseDto, BoardContext>> {
    return this.http.put<YearlyResource<BoardResponseDto, BoardContext>>(`${this.apiUrl}/${companyId}/board`, request);
  }

  deleteBoardMember(companyId: string, boardMemberId: string, body: BoardMemberDeleteRequest): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${companyId}/board/board-members/${boardMemberId}`, { body });
  }

  deleteExecutiveMember(companyId: string, executiveMemberId: string, body: BoardMemberDeleteRequest): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${companyId}/board/executive-members/${executiveMemberId}`, { body });
  }

  addOpbConnection(companyId: string, boardMemberId: string, opbCompanyId: string, year: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${companyId}/board/board-members/${boardMemberId}/opb-connections`, { opbCompanyId, year });
  }

  deleteOpbConnection(companyId: string, boardMemberId: string, opbCompanyId: string, year: number): Observable<void> {
    const params = new HttpParams().set('year', year.toString());
    return this.http.delete<void>(`${this.apiUrl}/${companyId}/board/board-members/${boardMemberId}/opb-connections/${opbCompanyId}`, { params });
  }

  addExecutiveOpbConnection(companyId: string, executiveMemberId: string, opbCompanyId: string, year: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${companyId}/board/executive-members/${executiveMemberId}/opb-connections`, { opbCompanyId, year });
  }

  deleteExecutiveOpbConnection(companyId: string, executiveMemberId: string, opbCompanyId: string, year: number): Observable<void> {
    const params = new HttpParams().set('year', year.toString());
    return this.http.delete<void>(`${this.apiUrl}/${companyId}/board/executive-members/${executiveMemberId}/opb-connections/${opbCompanyId}`, { params });
  }
}
